// src/pages/WalletLedgerPage.js
// Route: /patients/:patientId/wallet
//
// 28 Jul 2026 — new. Navigator-facing "Wallet Ledger" — same underlying data
// and same backend endpoint as the patient-facing "Transaction History"
// (PatientWalletPage.jsx's walletAPI.getTransactions), just with more
// columns and detail layered on top: reference links straight to the
// order/appointment, gateway provider/order/payment IDs for cross-checking
// against Razorpay directly, and a client-side reconciliation check.
//
// Deliberately the SAME data source, not a separate query — Deepu's
// explicit direction, 28 Jul 2026: one unified ledger, not two different
// views that could ever disagree with each other.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { walletAPI, patientAPI } from '../services/api';

import Icon from '../components/Icon';
const PAGE_SIZE = 50;

const TYPE_META = {
  WALLET_CREDIT_TOPUP:      { label: 'Top-up',                 sign: '+', color: 'var(--green)' },
  WALLET_CREDIT_REFUND:     { label: 'Refund',                  sign: '+', color: 'var(--green)' },
  WALLET_CREDIT_ADJUSTMENT: { label: 'Adjustment credit',       sign: '+', color: 'var(--green)' },
  WALLET_DEBIT_LAB_ORDER:   { label: 'Lab order (wallet)',      sign: '−', color: 'var(--red)' },
  WALLET_DEBIT_APPOINTMENT: { label: 'Appointment (wallet)',    sign: '−', color: 'var(--red)' },
  WALLET_DEBIT_ADJUSTMENT:  { label: 'Adjustment debit',        sign: '−', color: 'var(--red)' },
  RAZORPAY_LAB_ORDER:       { label: 'Lab order (Razorpay)',    sign: '−', color: 'var(--red)' },
  RAZORPAY_APPOINTMENT:     { label: 'Appointment (Razorpay)',  sign: '−', color: 'var(--red)' },
};

const TYPE_FILTER_OPTIONS = [
  { value: 'ALL',    label: 'All types' },
  { value: 'CREDIT', label: 'Credits (top-ups, refunds)' },
  { value: 'DEBIT',  label: 'Debits (orders, appointments)' },
  { value: 'RAZORPAY', label: 'Gateway-paid only' },
];

function formatRupees(minorUnits) {
  if (minorUnits === null || minorUnits === undefined) return '—';
  return `₹${(minorUnits / 100).toLocaleString('en-IN')}`;
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function matchesFilter(entry, filter) {
  if (filter === 'ALL') return true;
  if (filter === 'CREDIT') return entry.type.includes('CREDIT');
  if (filter === 'DEBIT') return entry.type.includes('DEBIT') && !entry.type.startsWith('RAZORPAY');
  if (filter === 'RAZORPAY') return entry.type.startsWith('RAZORPAY');
  return true;
}

export default function WalletLedgerPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [patient, setPatient] = useState(null);
  const [balanceMinorUnits, setBalanceMinorUnits] = useState(null);
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError('');
    try {
      const [patientRes, balanceRes, txnRes] = await Promise.all([
        patientAPI.get(patientId),
        walletAPI.getBalance(patientId),
        walletAPI.getTransactions(patientId, 0, PAGE_SIZE),
      ]);
      setPatient(patientRes.data);
      setBalanceMinorUnits(balanceRes.data.balanceMinorUnits);
      setEntries(txnRes.data.transactions || []);
      setPage(0);
      setTotalPages(txnRes.data.totalPages ?? 1);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load this patient's wallet ledger.");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (loadingMore || page + 1 >= totalPages) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await walletAPI.getTransactions(patientId, nextPage, PAGE_SIZE);
      setEntries(prev => [...prev, ...(res.data.transactions || [])]);
      setPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  };

  // 28 Jul 2026 — new. Client-side reconciliation check — the same "does the
  // ledger sum match the stored balance" question from the test plan's
  // Group E, but visible on screen instead of a manual SQL query. Only
  // counts WALLET_-prefixed entries — RAZORPAY_* rows are gateway-only and
  // never affected the wallet balance in the first place, so including them
  // here would make a genuinely-reconciled ledger look broken.
  const reconciliation = React.useMemo(() => {
    if (balanceMinorUnits === null || page + 1 < totalPages) return null; // only meaningful once all pages are loaded
    const computed = entries
      .filter(e => e.type.startsWith('WALLET_'))
      .reduce((sum, e) => sum + (e.type.includes('CREDIT') ? e.amountMinorUnits : -e.amountMinorUnits), 0);
    return { computed, matches: computed === balanceMinorUnits };
  }, [entries, balanceMinorUnits, page, totalPages]);

  const filteredEntries = entries.filter(e => matchesFilter(e, typeFilter));

  const goToReference = (entry) => {
    if (!entry.referenceId) return;
    if (entry.referenceType === 'APPOINTMENT') navigate(`/appointments/${entry.referenceId}`);
    else if (entry.referenceType === 'LAB_ORDER') navigate(`/lab-tests/${entry.referenceId}`);
  };

  return (
    <AppShell>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 20px 48px' }}>
        <span className="back-link" onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
          ← Back
        </span>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 12, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Wallet Ledger
            </div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {patient ? `${patient.firstName || ''} ${patient.lastName || ''}`.trim() : 'Loading…'}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Current balance</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{formatRupees(balanceMinorUnits)}</div>
          </div>
        </div>

        {error && (
          <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: 'var(--red)', fontSize: 13 }}>
            <Icon name="alert" size={14} /> {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>Loading ledger…</div>
        ) : (
          <>
            {reconciliation && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16,
                padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                background: reconciliation.matches ? 'var(--green-bg)' : 'var(--red-bg)',
                color: reconciliation.matches ? 'var(--green)' : 'var(--red)',
              }}>
                {reconciliation.matches
                  ? '✓ Reconciled — ledger matches stored balance'
                  : `⚠ Mismatch — ledger sums to ${formatRupees(reconciliation.computed)}, stored balance is ${formatRupees(balanceMinorUnits)}`}
              </div>
            )}
            {page + 1 < totalPages && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>
                Load the full history (below) to see the reconciliation check.
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {TYPE_FILTER_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTypeFilter(opt.value)}
                  style={{
                    padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 500,
                    border: `1px solid ${typeFilter === opt.value ? 'var(--brand-dark)' : 'var(--border)'}`,
                    background: typeFilter === opt.value ? 'var(--brand-dark)' : 'var(--bg-surface)',
                    color: typeFilter === opt.value ? 'var(--text-on-brand-dark)' : 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-canvas)', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>
                    <th style={{ padding: '10px 14px', borderRight: '1px solid var(--border)' }}>Date</th>
                    <th style={{ padding: '10px 14px', borderRight: '1px solid var(--border)' }}>Type</th>
                    <th style={{ padding: '10px 14px', borderRight: '1px solid var(--border)' }}>Description</th>
                    <th style={{ padding: '10px 14px', borderRight: '1px solid var(--border)' }}>Reference</th>
                    <th style={{ padding: '10px 14px', borderRight: '1px solid var(--border)' }}>Gateway</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', borderRight: '1px solid var(--border)' }}>Amount</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Balance after</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      No transactions match this filter.
                    </td></tr>
                  )}
                  {filteredEntries.map((entry) => {
                    const meta = TYPE_META[entry.type] || { label: entry.type, sign: '', color: 'var(--text-primary)' };
                    return (
                      <tr key={entry.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'var(--text-secondary)', borderRight: '1px solid var(--border)' }}>
                          {formatDateTime(entry.createdAt)}
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 500, borderRight: '1px solid var(--border)' }}>{meta.label}</td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', maxWidth: 260, borderRight: '1px solid var(--border)' }}>
                          {entry.description}
                        </td>
                        <td style={{ padding: '10px 14px', borderRight: '1px solid var(--border)' }}>
                          {entry.referenceId ? (
                            <span
                              onClick={() => goToReference(entry)}
                              style={{ color: 'var(--blue)', cursor: 'pointer', textDecoration: 'underline', fontSize: 12 }}
                            >
                              {entry.referenceType} →
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 11, color: 'var(--text-muted)', borderRight: '1px solid var(--border)' }}>
                          {entry.gatewayPaymentId ? (
                            <span title={`Order: ${entry.gatewayOrderId || '—'}`}>
                              {entry.gatewayProvider || 'Razorpay'} · {entry.gatewayPaymentId}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: meta.color, whiteSpace: 'nowrap', borderRight: '1px solid var(--border)' }}>
                          {meta.sign}{formatRupees(entry.amountMinorUnits)}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                          {formatRupees(entry.balanceAfterMinorUnits)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {page + 1 < totalPages && (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                style={{
                  marginTop: 16, width: '100%', padding: '10px', borderRadius: 8,
                  border: '1px solid var(--border)', background: 'var(--bg-subtle)', fontSize: 13, fontWeight: 500,
                  cursor: loadingMore ? 'default' : 'pointer', opacity: loadingMore ? 0.6 : 1,
                }}
              >
                {loadingMore ? 'Loading…' : `Load more (page ${page + 2} of ${totalPages})`}
              </button>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
